// Locale Switcher component
import { SUPPORTED_LOCALES } from '../content/config';
import { i18n } from '../utils/i18n';
import { appState } from '../utils/appState';
import { analytics } from '../utils/analytics';
import type { SupportedLocale } from '../types/locale';

export class LocaleSwitcher {
  private container: HTMLElement;

  constructor(containerSelector: string) {
    const element = document.querySelector(containerSelector);
    if (!element) throw new Error(`Element "${containerSelector}" not found`);

    this.container = element as HTMLElement;
  }

  init(): void {
    this.render();
    this.setupEventListeners();

    // Re-render on locale change
    window.addEventListener('localechange', () => {
      this.render();
    });
  }

  private render(): void {
    const currentLocale = i18n.getCurrentLocale();

    const localeElements = SUPPORTED_LOCALES.map((locale) => {
      const isCurrent = locale.code === currentLocale;

      if (isCurrent) {
        return `<span class="locale-current">${locale.code.toUpperCase()}</span>`;
      }

      return `<a href="?lang=${locale.code}" class="locale-link" data-locale="${locale.code}">${locale.code.toUpperCase()}</a>`;
    });

    this.container.innerHTML = `
      <div class="locale-switcher">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M7.548 9.31499L8.182 7.50999C7.52619 7.30981 6.89671 7.03183 6.307 6.68199C6.74654 6.19439 7.11946 5.65065 7.416 5.06499C7.72721 4.45389 7.95716 3.80472 8.1 3.13399C8.689 3.13399 9.079 3.15399 9.519 3.19399V1.38499C9.03748 1.43702 8.55329 1.46039 8.069 1.45499H5.687V0.844985C5.68665 0.640162 5.70675 0.435816 5.747 0.234985H3.738C3.77922 0.439046 3.79932 0.646808 3.798 0.854985V1.45499H1.589C1.10504 1.46013 0.621206 1.43676 0.14 1.38499V3.19399C0.64 3.15399 0.979 3.13399 1.569 3.13399C1.73855 3.72623 1.96982 4.29904 2.259 4.84299C2.60815 5.50559 3.0414 6.12034 3.548 6.67199C2.45071 7.31187 1.24995 7.75488 0 7.98099C0.420415 8.51019 0.744977 9.10889 0.959 9.74998C2.3811 9.37004 3.72712 8.74794 4.938 7.91099C5.74428 8.48883 6.62146 8.9607 7.548 9.31499ZM3.488 3.13399H6.137C5.92419 4.03002 5.495 4.86024 4.887 5.55199C4.22732 4.87451 3.74655 4.04355 3.488 3.13399Z" fill="#64748B"></path>
      <path d="M13.7168 15.765L12.9918 13.386H9.34882L8.62382 15.765H6.34082L9.86782 5.72998H12.4588L15.9998 15.765H13.7168ZM12.4858 11.609C11.8192 9.45298 11.4422 8.23398 11.3548 7.95198C11.2708 7.66898 11.2098 7.44598 11.1738 7.28198C11.0232 7.86465 10.5925 9.30698 9.88182 11.609H12.4858Z" fill="#64748B"></path>
    </svg>
        ${localeElements.join('')}
      </div>
    `;
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      
      if (target.classList.contains('locale-link')) {
        e.preventDefault();
        const newLocale = target.dataset.locale as SupportedLocale;
        
        if (newLocale) {
          const oldLocale = i18n.getCurrentLocale();
          analytics.trackLocaleChange(oldLocale, newLocale);
          
          appState.locale.set(newLocale);
          await i18n.switchLocale(newLocale);
        }
      }
    });
  }
}
